import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { trpc } from '../lib/trpc';
import { setToken, setRefreshToken } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

export function SetupPage() {
  const navigate = useNavigate();
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [noEmail, setNoEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const completeSetup = trpc.auth.completeSetup.useMutation({
    onSuccess: (data) => {
      setToken(data.accessToken);
      setRefreshToken(data.refreshToken);
      sessionStorage.removeItem('setupToken');
      toast.success('Account setup complete!');
      navigate('/dashboard');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to complete setup');
      setIsLoading(false);
    },
  });

  useEffect(() => {
    const token = sessionStorage.getItem('setupToken');
    if (!token) {
      toast.error('Invalid session. Please login again.');
      navigate('/login');
    } else {
      setSetupToken(token);
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setupToken) return;

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);
    
    const finalEmail = noEmail ? 'support@unidom.az' : email;

    completeSetup.mutate({
      setupToken,
      email: finalEmail,
      password,
    });
  };

  if (!setupToken) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-muted/50 shadow-xl bg-card/50 backdrop-blur-sm">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold tracking-tight">Complete Setup</CardTitle>
          <CardDescription>
            Set up your account credentials to continue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={noEmail ? 'support@unidom.az' : email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={noEmail || isLoading}
                required={!noEmail}
                className="bg-background/50"
              />
              <div className="flex items-center space-x-2 pt-1">
                <input
                  type="checkbox"
                  id="no-email"
                  checked={noEmail}
                  onChange={(e) => setNoEmail(e.target.checked)}
                  disabled={isLoading}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="no-email" className="text-sm font-normal text-muted-foreground cursor-pointer">
                  I do not have an email address
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                required
                minLength={6}
                className="bg-background/50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                required
                minLength={6}
                className="bg-background/50"
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Setting up...' : 'Complete Setup'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
